import { supabase } from '@/lib/supabase';
import type { Database, QuestionnaireRow } from '@/types/database';

type QuestionnaireInsert = Database['public']['Tables']['questionnaires']['Insert'];
type QuestionnaireUpdate = Database['public']['Tables']['questionnaires']['Update'];

export type QuestionnaireKey =
  | 'styleWords' | 'mustHaves' | 'dealBreakers' | 'budgetPriorities'
  | 'likes' | 'dislikes' | 'inspiration' | 'notes';

export type QuestionnaireResponses = Partial<Record<QuestionnaireKey, string>>;

export const QUESTIONNAIRE_FIELDS: { key: QuestionnaireKey; label: string; placeholder: string }[] = [
  { key: 'styleWords',       label: 'Three style words',       placeholder: 'e.g. warm, organic, refined' },
  { key: 'mustHaves',        label: 'Must-haves',              placeholder: 'Non-negotiables for the project' },
  { key: 'dealBreakers',     label: 'Deal-breakers',           placeholder: 'What to absolutely avoid' },
  { key: 'budgetPriorities', label: 'Budget priorities',       placeholder: 'Where to splurge vs. save' },
  { key: 'likes',            label: 'Likes',                   placeholder: 'Materials, colors, references the client loves' },
  { key: 'dislikes',         label: 'Dislikes',                placeholder: 'Things that make them recoil' },
  { key: 'inspiration',      label: 'Inspiration sources',     placeholder: 'Pinterest boards, magazines, past homes…' },
  { key: 'notes',            label: 'Other notes',             placeholder: 'Anything else worth capturing' },
];

const DEFAULT_TITLE = 'Style Questionnaire';

export async function getQuestionnaire(projectId: string): Promise<QuestionnaireRow | null> {
  const { data, error } = await supabase
    .from('questionnaires')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertQuestionnaire(
  projectId: string,
  responses: QuestionnaireResponses,
  existingId: string | null,
  markSubmitted = false,
): Promise<QuestionnaireRow> {
  if (existingId) {
    const patch: QuestionnaireUpdate = { responses };
    if (markSubmitted) patch.submitted_at = new Date().toISOString();
    const { data, error } = await supabase
      .from('questionnaires').update(patch).eq('id', existingId).select().single();
    if (error) throw error;
    return data;
  }
  const insert: QuestionnaireInsert = {
    project_id: projectId,
    title: DEFAULT_TITLE,
    responses,
    submitted_at: markSubmitted ? new Date().toISOString() : null,
  };
  const { data, error } = await supabase
    .from('questionnaires').insert(insert).select().single();
  if (error) throw error;
  return data;
}

export function readResponses(row: { responses: unknown } | null): QuestionnaireResponses {
  if (!row) return {};
  const r = row.responses as QuestionnaireResponses | null;
  return r && typeof r === 'object' ? r : {};
}
